import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableHead,
  Typography,
} from '@mui/material';
import { TableBody, TableCell, TableRow } from '@mui/material';
import dayjs from 'dayjs';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import deviceApi from '~/apis/device';
import facilityApi from '~/apis/facility';
import userApi from '~/apis/user';
import { CloseIcon, InformationFillIcon, RotationLock2 } from '~/assets/icons';
import { BaseScrollbar } from '~/components/common';
import BaseRow from '~/components/common/base-row/BaseRow';
import { cast, handleApiError } from '~/utils/helper';
import { IEquipment } from '~/utils/interface/equipment';
import { IFacility } from '~/utils/interface/facility';
import { ISessionByUser, IUser } from '~/utils/interface/user';
import ResetPasswordDialog from '../reset-password-dialog/ResetPasswordDialog';
import DetailFacilityDialog from '~/pages/admin/facilities/components/detail-facility-dialog/DetailFacilityDialog';
import DetailEquipmentDialog from '~/pages/admin/equipment/component/detail-equipment-dialog/DetailEquipmentDialog';
import feedbackApi from '~/apis/feedback';
import CustomerDetailDialog from '~/pages/admin/feedback/components/customer-detail-dialog/CustomerDetailDialog';
import { IFeedback } from '~/utils/interface/feedback';

type ShowUserDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  user?: IUser;
};

const ShowUserDialog = (props: ShowUserDialogProps) => {
  const { t } = useTranslation('admin/user');

  const { user } = props;

  const [sessionByUsers, setSessionByUsers] = useState<Array<ISessionByUser>>([]);
  const [facilityList, setFacilityList] = useState<IFacility[]>([]);
  const [facility, setFacility] = useState<IFacility>();

  const [equipment, setEquipment] = useState<IEquipment>();
  const [equipmentList, setEquipmentList] = useState<IEquipment[]>([]);

  const [isOpenDetailFacilityDialog, setIsOpenDetailFacilityDialog] = useState<boolean>(false);
  const [isOpenDetailEquipmentDialog, setIsOpenDetailEquipmentDialog] = useState<boolean>(false);
  const [openResetPasswordDialog, setOpenResetPasswordDialog] = useState<boolean>(false);

  const [feedbackList, setFeedbackList] = useState<Array<IFeedback>>([]);
  const [feedback, setFeedback] = useState<IFeedback>();
  const [isOpenCustomerFeedbackDetail, setIsOpenCustomerFeedbackDetail] = useState<boolean>(false);

  useEffect(() => {
    if (props.open && props.user) {
      (async () => {
        try {
          const res = await userApi.getSessionByUser(cast(user?.username));
          setSessionByUsers(res.data);
        } catch (error) {
          handleApiError(error);
        }
      })();

      (async () => {
        try {
          const res = await facilityApi.getListFacilityByUser(String(user?.username));
          setFacilityList(res.data);
        } catch (error) {
          handleApiError(error);
        }
      })();

      (async () => {
        try {
          const res = await deviceApi.getByUserList(String(user?.username));
          setEquipmentList(res.data);
        } catch (error) {
          handleApiError(error);
        }
      })();

      (async () => {
        try {
          const res = await feedbackApi.getList({ username: user?.username });
          setFeedbackList(res.data);
        } catch (error) {
          handleApiError(error);
        }
      })();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, user?.username]);

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const getStatus = (status: any) => {
    if (status === 0) {
      return (
        <Box className='text-[#FF9500] border border-[#FF9500] rounded-[20px] leading-[2px] p-2.5'>
          {t('status.new')}
        </Box>
      );
    }

    if (status === 1) {
      return (
        <Box className='text-[#3D9149] border border-[#3D9149] rounded-[20px]  leading-[2px] p-2.5'>
          {t('status.active')}
        </Box>
      );
    }
    if (status === 2) {
      return (
        <Box className='text-[#005F73] border border-[#005F73] rounded-[20px] leading-[2px] p-2.5'>
          {t('status.friendly')}
        </Box>
      );
    }
    if (status === 3) {
      return (
        <Box className='text-[#72777B] border border-[#72777B] rounded-[20px] leading-[2px] p-2.5'>
          {t('status.block')}
        </Box>
      );
    }

    return '';
  };

  return (
    <Box className='w-full'>
      <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='lg'>
        <DialogTitle className='bg-[#005F73] w-full'>
          <Box className='flex items-center'>
            <Typography fontWeight={'700'} fontSize={'20px'} marginRight={'22px'}>
              {t('show.title')}
            </Typography>
            <RotationLock2 className='cursor-pointer' onClick={() => setOpenResetPasswordDialog(true)} />
          </Box>
          <IconButton onClick={() => handleSetOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className='!ps-0 !pt-0'>
          <BaseRow>
            <Box className='w-[55%] h-full relative border-r border-solid border-[#DFDFDF]'>
              <Box className='flex flex-row ps-[44px] gap-[15px] mb-[16px] mt-[24px]'>
                <Box className='font-bold'>{t('show.fullName')}:</Box>
                <Box>{user?.name}</Box>
              </Box>

              <Box className='flex flex-row ps-[44px] gap-[15px] mb-[16px]'>
                <Box className='font-bold'>{t('show.phone')}:</Box>
                <Box>{user?.username}</Box>
              </Box>

              <Box className='flex flex-row ps-[44px] gap-[15px] mb-[16px]'>
                <Box className='font-bold'>{t('show.email')}:</Box>
                <Box>{user?.email}</Box>
              </Box>

              <Box className='flex flex-row ps-[44px] gap-[15px] mb-[16px]'>
                <Box className='font-bold'>{t('show.dateOfBirth')}:</Box>
                <Box>{user?.date_of_birth ? dayjs(user?.date_of_birth).format('DD/MM/YYYY HH:mm:ss') : ''}</Box>
              </Box>

              <Box className='flex flex-row ps-[44px] gap-[15px] mb-[16px] items-center'>
                <Box className='font-bold'>{t('show.status')}:</Box>
                <Box>{getStatus(user?.user_status)}</Box>
              </Box>

              <Box className='flex flex-row ps-[44px] gap-[15px] mb-[16px]'>
                <Box className='font-bold'>{t('show.createdAt')}:</Box>
                <Box>{user?.created_at ? dayjs(user?.created_at).format('DD/MM/YYYY HH:mm:ss') : ''}</Box>
              </Box>

              <Box className='flex flex-row ps-[44px] gap-[15px] mb-[16px]'>
                <Box className='font-bold'>{t('show.technician')}:</Box>
                <Box>{user?.technician}</Box>
              </Box>

              <Divider
                sx={{
                  borderColor: '#DFDFDF',
                  borderBottomWidth: 1,
                  width: '100%',
                  mt: 3,
                  mb: 2,
                  height: '100%',
                }}
              ></Divider>

              <Box className='w-full px-[42px] mb-5'>
                <BaseRow className='mb-[13px] gap-[15px] w-full !justify-between !items-center'>
                  <Box className='font-bold text-[#005F73] text-[18px]'>{t('show.historyFeedback')}</Box>
                  <Box className='rounded-full text-white  h-[25px] flex items-center justify-center font-bold bg-[#005F73] p-2'>
                    {feedbackList?.length}
                  </Box>
                </BaseRow>
                {feedbackList?.length ? (
                  <BaseScrollbar maxHeight='400px'>
                    <Box className='w-full flex flex-col gap-[15px]'>
                      <Table
                        stickyHeader
                        className='[&_td]:border-b [&_th]:border-b [&_td]:border-[#005F73] [&_th]:border-[#005F73]'
                      >
                        <TableHead className='[&_td]:border-b [&_td]:border-[#005F73]'>
                          <TableRow className='h-[30px]'>
                            <TableCell align='center' className='font-bold py-[6px] px-0 '>
                              {t('show.sentDate')}
                            </TableCell>
                            <TableCell align='center' className='font-bold py-[6px] px-0'>
                              {t('show.typeComment')}
                            </TableCell>
                            <TableCell align='center' className='font-bold py-[6px] px-0'>
                              {t('show.score')}
                            </TableCell>
                            <TableCell align='center' className='font-bold py-[6px] px-0'>
                              {t('action')}
                            </TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {feedbackList.map((row: IFeedback, index: number) => {
                            return (
                              <TableRow key={index}>
                                <TableCell align='center' className='py-[6px] px-0'>
                                  {row.submit_time ? dayjs(row.submit_time).format('DD/MM/YYYY') : ''}
                                </TableCell>
                                <TableCell align='center' className='py-[6px] px-0'>
                                  {row.type_of_feedback}
                                </TableCell>
                                <TableCell align='center' className='py-[6px] px-0'>
                                  {row.rate_experience}
                                </TableCell>
                                <TableCell align='center' className='py-[6px] px-0'>
                                  <Button
                                    className='text-white bg-[#005F73] hover:bg-[#005F73] px-[14px] rounded-[4px] hover:opacity-80'
                                    onClick={() => {
                                      setIsOpenCustomerFeedbackDetail(true);
                                      setFeedback(row);
                                    }}
                                  >
                                    {t('moreDetail')}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>
                  </BaseScrollbar>
                ) : null}
              </Box>

              <Divider
                sx={{
                  borderColor: '#DFDFDF',
                  borderBottomWidth: 1,
                  width: '100%',
                  mt: 3,
                  mb: 2,
                  height: '100%',
                }}
              ></Divider>

              <Box className='px-[42px]'>
                <Box className='mb-[36px]'>
                  <BaseRow className='mb-[13px] gap-[15px] w-full !justify-between !items-center'>
                    <Box className='font-bold text-[#005F73] text-[18px]'>{t('show.facilityList')}</Box>
                    <Box className='rounded-full text-white w-[25px] h-[25px] flex items-center justify-center font-bold bg-[#005F73] p-2'>
                      {facilityList?.length}
                    </Box>
                  </BaseRow>
                  <BaseScrollbar maxHeight='250px'>
                    {facilityList?.length ? (
                      <Box className='rounded-[10px] overflow-hidden border-[1px] border-[#005F73]'>
                        <Table>
                          <TableBody className='divide-y divide-[#005F73]'>
                            {facilityList.map((row, index) => {
                              return (
                                <TableRow key={index}>
                                  <TableCell className='border-none'>
                                    <Box className='font-bold'>{row.location_name}</Box>
                                    <Box>{row.full_address}</Box>
                                  </TableCell>
                                  <TableCell className='border-none text-right'>
                                    <Box className='font-bold'>
                                      <Box component='span' className='font-bold text-[#ED1002]'>
                                        {row.device_count}
                                      </Box>{' '}
                                      {t('show.device')}
                                    </Box>
                                    <Box className='font-bold'>
                                      {row.deployment_status === 2 ? (
                                        <Box className='text-[#3D9149]'>{t('facilityStatus.activated')}</Box>
                                      ) : row.deployment_status === 1 ? (
                                        <Box className='text-[#3D9149]'>{t('facilityStatus.deployed')}</Box>
                                      ) : (
                                        <Box className='text-[#72777B]'>{t('facilityStatus.pending')}</Box>
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell className='border-none w-[80px]'>
                                    <IconButton>
                                      <InformationFillIcon
                                        fill='#D9D9D9'
                                        onClick={() => {
                                          setFacility(row);
                                          setIsOpenDetailFacilityDialog(true);
                                        }}
                                      />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Box>
                    ) : null}
                  </BaseScrollbar>
                </Box>
              </Box>
            </Box>

            <Box className='w-[45%] mt-[24px] ml-[24px]'>
              <Box className='mb-[36px]'>
                <BaseRow className='mb-[13px] gap-[15px] w-full !justify-between !items-center'>
                  <Box className='font-bold text-[#005F73] text-[18px]'>{t('show.loginSession')}</Box>
                </BaseRow>
                {sessionByUsers?.length ? (
                  <BaseScrollbar maxHeight='400px'>
                    <Box className='rounded-[10px] overflow-hidden border-[1px] border-[#005F73]'>
                      <Table>
                        <TableBody className='divide-y divide-[#005F73]'>
                          {sessionByUsers.map((row: ISessionByUser, index: any) => {
                            return (
                              <TableRow key={index}>
                                <TableCell className='border-none'>
                                  <Box>
                                    <Box className='font-bold mb-[5px]'>{`${cast(row.user_os)} - ${
                                      row.user_os_version
                                    }`}</Box>
                                    <Box className='flex'>
                                      <Typography fontWeight={'700'} marginRight={'5px'}>
                                        {t('show.location')}:
                                      </Typography>
                                      <Typography>{cast(row.location)}</Typography>
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className='border-none text-right flex items-center justify-end gap-[10px]'>
                                  <Box>
                                    <Box className='mb-[5px]'>{cast(row.login_time)}</Box>
                                    <Box className='flex'>
                                      <Typography fontWeight={'700'}>{t('show.ip')}:</Typography>
                                      <Typography>{cast(row.ip_address)}</Typography>
                                    </Box>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>
                  </BaseScrollbar>
                ) : null}
              </Box>

              <Box className='w-full'>
                <BaseRow className='mb-[13px] gap-[15px] w-full !justify-between !items-center'>
                  <Box className='font-bold text-[#005F73] text-[18px]'>{t('show.listEquipment')}</Box>
                  <Box className='rounded-full text-white w-[25px] h-[25px] flex items-center justify-center font-bold bg-[#005F73] p-2'>
                    {equipmentList?.length}
                  </Box>
                </BaseRow>
                <BaseScrollbar maxHeight='300px'>
                  {equipmentList?.length ? (
                    <Box className='rounded-[10px] overflow-hidden border-[1px] border-[#005F73]'>
                      <Table>
                        <TableBody className='divide-y divide-[#005F73]'>
                          {equipmentList.map((row, index) => {
                            return (
                              <TableRow key={index}>
                                <TableCell className='border-none'>
                                  <Box className='font-bold'>{row.device_code}</Box>
                                  <Box>{row.location_name}</Box>
                                </TableCell>
                                <TableCell className='border-none text-right'>
                                  <Box className='font-bold'>{row.device_name}</Box>
                                  <Box className='font-bold'>
                                    <Box className={row.connect_status === 1 ? 'text-[#3D9149]' : 'text-[#F32A4C]'}>
                                      {t('connectionStatus.' + row.connect_status)}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className='border-none w-[80px]'>
                                  <IconButton>
                                    <InformationFillIcon
                                      fill='#D9D9D9'
                                      onClick={() => {
                                        setEquipment(row);
                                        setIsOpenDetailEquipmentDialog(true);
                                      }}
                                    />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>
                  ) : null}
                </BaseScrollbar>
              </Box>
            </Box>
          </BaseRow>
        </DialogContent>
      </Dialog>

      <DetailFacilityDialog open={isOpenDetailFacilityDialog} data={facility} setOpen={setIsOpenDetailFacilityDialog} />
      <DetailEquipmentDialog
        open={isOpenDetailEquipmentDialog}
        setOpen={setIsOpenDetailEquipmentDialog}
        data={equipment}
      />
      <ResetPasswordDialog open={openResetPasswordDialog} setOpen={setOpenResetPasswordDialog} user={user} />
      <CustomerDetailDialog
        open={isOpenCustomerFeedbackDetail}
        setOpen={setIsOpenCustomerFeedbackDetail}
        data={feedback}
      />
    </Box>
  );
};

export default memo(ShowUserDialog);
